import { createFoilRng, hashFoilSeed } from "./aiShine";

const TOPOGRAPHY_SIZE = 320;
const GRID_SIZE = 6;
const OCTAVES = 4;
const PERSISTENCE = 0.48;
const LACUNARITY = 2;
const MIN_LEVELS = 11;
const MAX_LEVELS = 16;
const MAJOR_LINE_STEP = 4;

const topographyCache = new Map();

function hash2D(x, y, seed) {
  let h = seed | 0;
  h ^= Math.imul(x | 0, 374761393);
  h ^= Math.imul(y | 0, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

function mod(n, m) {
  return ((n % m) + m) % m;
}

function smoothstep(t) {
  return t * t * (3 - 2 * t);
}

function periodicValueNoise(x, y, period, seed) {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;

  const sx = smoothstep(x - x0);
  const sy = smoothstep(y - y0);

  const ax = mod(x0, period);
  const bx = mod(x1, period);
  const ay = mod(y0, period);
  const by = mod(y1, period);

  const n00 = hash2D(ax, ay, seed);
  const n10 = hash2D(bx, ay, seed);
  const n01 = hash2D(ax, by, seed);
  const n11 = hash2D(bx, by, seed);

  const ix0 = n00 + (n10 - n00) * sx;
  const ix1 = n01 + (n11 - n01) * sx;

  return ix0 + (ix1 - ix0) * sy;
}

function fbm(x, y, seed, basePeriod) {
  let value = 0;
  let amplitude = 1;
  let amplitudeSum = 0;
  let frequency = 1;

  for (let octave = 0; octave < OCTAVES; octave += 1) {
    const period = Math.max(1, Math.round(basePeriod * frequency));
    value +=
      periodicValueNoise(
        x * frequency,
        y * frequency,
        period,
        seed + octave * 1013,
      ) * amplitude;
    amplitudeSum += amplitude;
    amplitude *= PERSISTENCE;
    frequency *= LACUNARITY;
  }

  return value / amplitudeSum;
}

function generateField(seed, basePeriod, detailMix) {
  const cols = Math.ceil(TOPOGRAPHY_SIZE / GRID_SIZE) + 1;
  const rows = Math.ceil(TOPOGRAPHY_SIZE / GRID_SIZE) + 1;
  const field = Array.from({ length: rows }, () => new Float32Array(cols));

  let min = Infinity;
  let max = -Infinity;

  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      const nx = (x / (cols - 1)) * basePeriod;
      const ny = (y / (rows - 1)) * basePeriod;
      const large = fbm(nx, ny, seed, basePeriod);
      const detail = fbm(
        nx * 1.8 + 17.31,
        ny * 1.8 + 9.73,
        seed + 8273,
        basePeriod,
      );
      const value = large * (1 - detailMix) + detail * detailMix;

      field[y][x] = value;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }

  const range = max - min || 1;

  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      field[y][x] = (field[y][x] - min) / range;
    }
  }

  return { field, rows, cols };
}

function interpolate(a, b, threshold) {
  const denominator = b - a;
  if (Math.abs(denominator) < 0.000001) return 0.5;
  return Math.max(0, Math.min(1, (threshold - a) / denominator));
}

function contourSegments(data, threshold) {
  const { field, rows, cols } = data;
  const segments = [];

  for (let y = 0; y < rows - 1; y += 1) {
    for (let x = 0; x < cols - 1; x += 1) {
      const tl = field[y][x];
      const tr = field[y][x + 1];
      const br = field[y + 1][x + 1];
      const bl = field[y + 1][x];

      const index =
        (tl >= threshold ? 8 : 0) |
        (tr >= threshold ? 4 : 0) |
        (br >= threshold ? 2 : 0) |
        (bl >= threshold ? 1 : 0);

      if (index === 0 || index === 15) continue;

      const px = x * GRID_SIZE;
      const py = y * GRID_SIZE;

      const topT = interpolate(tl, tr, threshold);
      const rightT = interpolate(tr, br, threshold);
      const bottomT = interpolate(bl, br, threshold);
      const leftT = interpolate(tl, bl, threshold);

      const points = {
        top: [px + topT * GRID_SIZE, py],
        right: [px + GRID_SIZE, py + rightT * GRID_SIZE],
        bottom: [px + bottomT * GRID_SIZE, py + GRID_SIZE],
        left: [px, py + leftT * GRID_SIZE],
      };

      const highCenter = (tl + tr + br + bl) * 0.25 >= threshold;
      const push = (a, b) => {
        segments.push([points[a], points[b]]);
      };

      switch (index) {
        case 1:
          push("left", "bottom");
          break;
        case 2:
          push("bottom", "right");
          break;
        case 3:
          push("left", "right");
          break;
        case 4:
          push("top", "right");
          break;
        case 5:
          if (highCenter) {
            push("top", "left");
            push("bottom", "right");
          } else {
            push("top", "right");
            push("left", "bottom");
          }
          break;
        case 6:
          push("top", "bottom");
          break;
        case 7:
          push("top", "left");
          break;
        case 8:
          push("left", "top");
          break;
        case 9:
          push("top", "bottom");
          break;
        case 10:
          if (highCenter) {
            push("top", "right");
            push("left", "bottom");
          } else {
            push("top", "left");
            push("bottom", "right");
          }
          break;
        case 11:
          push("top", "right");
          break;
        case 12:
          push("left", "right");
          break;
        case 13:
          push("bottom", "right");
          break;
        case 14:
          push("left", "bottom");
          break;
        default:
          break;
      }
    }
  }

  return segments;
}

function pointKey(point) {
  return `${point[0].toFixed(2)},${point[1].toFixed(2)}`;
}

function polylineToPath(points) {
  let path = `M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`;
  for (let i = 1; i < points.length; i += 1) {
    path += `L${points[i][0].toFixed(1)},${points[i][1].toFixed(1)}`;
  }
  const last = points[points.length - 1];
  return pointKey(points[0]) === pointKey(last) ? `${path}Z` : path;
}

function segmentsToPath(segments) {
  if (!segments.length) return "";

  const byPoint = new Map();
  segments.forEach((segment, index) => {
    segment.forEach((point) => {
      const key = pointKey(point);
      const list = byPoint.get(key);
      if (list) list.push(index);
      else byPoint.set(key, [index]);
    });
  });

  const used = new Array(segments.length).fill(false);

  const takeNext = (key) => {
    const list = byPoint.get(key);
    if (!list) return -1;
    for (let i = 0; i < list.length; i += 1) {
      if (!used[list[i]]) return list[i];
    }
    return -1;
  };

  const extend = (points, append) => {
    for (;;) {
      const end = append ? points[points.length - 1] : points[0];
      const next = takeNext(pointKey(end));
      if (next < 0) return;
      used[next] = true;
      const [a, b] = segments[next];
      const point = pointKey(a) === pointKey(end) ? b : a;
      if (append) points.push(point);
      else points.unshift(point);
    }
  };

  let path = "";
  for (let i = 0; i < segments.length; i += 1) {
    if (used[i]) continue;
    used[i] = true;
    const points = [segments[i][0], segments[i][1]];
    extend(points, true);
    extend(points, false);
    path += polylineToPath(points);
  }
  return path;
}

export function topographyPattern(cardId) {
  const key = String(cardId || "topography");
  const cached = topographyCache.get(key);
  if (cached) return cached;

  const seed = hashFoilSeed(key) >>> 0;
  const rand = createFoilRng(seed ^ 0x70f0a1);
  const levels =
    MIN_LEVELS + Math.floor(rand() * (MAX_LEVELS - MIN_LEVELS + 1));
  const basePeriod = 2 + Math.floor(rand() * 2);
  const detailMix = 0.1 + rand() * 0.1;
  const data = generateField(seed, basePeriod, detailMix);

  const lines = [];
  for (let i = 1; i <= levels; i += 1) {
    const threshold = i / (levels + 1);
    const d = segmentsToPath(contourSegments(data, threshold));
    if (!d) continue;
    lines.push({
      id: `${key}-${i}`,
      d,
      major: i % MAJOR_LINE_STEP === 0,
    });
  }

  const pattern = { size: TOPOGRAPHY_SIZE, lines };
  topographyCache.set(key, pattern);
  return pattern;
}
